/**
 * Anna & Klara — custom PDP behaviours (additive, Horizon-native patterns).
 *
 * Defines three custom elements used by the buybox blocks:
 *   <pdp-scarcity>     — re-renders itself when the selected variant changes.
 *   <pdp-delivery>     — computes the shipping/delivery date client-side (pages are cached).
 *   <pdp-add-to-cart>  — AJAX add-to-cart that refreshes and opens the cart drawer.
 *
 * No new dependencies: everything is pulled from the theme importmap.
 */
import { morph } from '@theme/morph';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/* ------------------------------------------------------------------ *
 * Scarcity — mirrors product-inventory.js: listen for the product
 * select event on the closest section and morph from the fresh HTML.
 * Server Liquid decides visibility, so the state is always correct.
 * ------------------------------------------------------------------ */
class PdpScarcity extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  #onSelect = (event) => {
    // Ignore selects coming from product cards (quick add etc.).
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html || !this.id) return;
        const fresh = detail.html.querySelector(`#${CSS.escape(this.id)}`);
        if (fresh) morph(this, fresh);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pdp-scarcity]', error);
      });
  };
}
if (!customElements.get('pdp-scarcity')) customElements.define('pdp-scarcity', PdpScarcity);

/* ------------------------------------------------------------------ *
 * Price + discount badge — re-renders itself on variant change so the
 * price, strikethrough compare-at and "−X%" badge stay correct.
 * ------------------------------------------------------------------ */
class PdpPrice extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  #onSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html || !this.id) return;
        const fresh = detail.html.querySelector(`#${CSS.escape(this.id)}`);
        if (fresh) morph(this, fresh);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pdp-price]', error);
      });
  };
}
if (!customElements.get('pdp-price')) customElements.define('pdp-price', PdpPrice);

/* ------------------------------------------------------------------ *
 * Delivery date — cutoff 14:00 Europe/Warsaw, Mon–Fri.
 * After cutoff / weekend -> ship next business day.
 * Delivery = ship + 1 business day. Rendered as "we wtorek, 22 lipca".
 * ------------------------------------------------------------------ */
const MONTHS_GEN = [
  'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
  'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia',
];
// Indexed by Date.getDay() (0 = Sunday). Accusative + preposition, per the mockup ("we wtorek").
const DOW_ACC = [
  'w niedzielę', 'w poniedziałek', 'we wtorek', 'w środę',
  'w czwartek', 'w piątek', 'w sobotę',
];

const isBusinessDay = (date) => {
  const day = date.getDay();
  return day >= 1 && day <= 5;
};

const nextBusinessDay = (date) => {
  const next = new Date(date);
  do {
    next.setDate(next.getDate() + 1);
  } while (!isBusinessDay(next));
  return next;
};

// Indexed by Date.getDay(). Abbreviations for the delivery range ("śr.–czw.").
const DOW_ABBR = ['niedz.', 'pon.', 'wt.', 'śr.', 'czw.', 'pt.', 'sob.'];

const formatPolishDate = (date) => `${DOW_ACC[date.getDay()]}, ${date.getDate()} ${MONTHS_GEN[date.getMonth()]}`;

/**
 * Formats a business-day delivery range, e.g. "śr.–czw., 22–23 lipca".
 * Same month -> month printed once ("22–23 lipca"); month turn -> both months
 * with spaced dash ("31 lipca – 1 sierpnia"). Returns nowrap-wrapped HTML.
 */
const formatDeliveryRange = (from, to) => {
  const dow = `${DOW_ABBR[from.getDay()]}–${DOW_ABBR[to.getDay()]}`;
  let dayMonth;
  if (from.getMonth() === to.getMonth()) {
    dayMonth = `${from.getDate()}–${to.getDate()} ${MONTHS_GEN[from.getMonth()]}`;
  } else {
    dayMonth = `${from.getDate()} ${MONTHS_GEN[from.getMonth()]} – ${to.getDate()} ${MONTHS_GEN[to.getMonth()]}`;
  }
  return `<span class="pdp-delivery__nowrap">${dow}</span>, <span class="pdp-delivery__nowrap">${dayMonth}</span>`;
};

/** Reads the current wall-clock date in Europe/Warsaw regardless of the viewer's timezone. */
const warsawNow = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const map = {};
  for (const part of parts) map[part.type] = part.value;
  // `hour` can come back as "24" at midnight in some engines.
  const hour = Number(map.hour) % 24;
  return { year: Number(map.year), month: Number(map.month), day: Number(map.day), hour };
};

class PdpDelivery extends HTMLElement {
  connectedCallback() {
    // Recompute on load and whenever the tab regains focus (a cached page may be stale).
    this.#render();
    this.#onVisible = () => {
      if (document.visibilityState === 'visible') this.#render();
    };
    document.addEventListener('visibilitychange', this.#onVisible);
  }

  disconnectedCallback() {
    if (this.#onVisible) document.removeEventListener('visibilitychange', this.#onVisible);
  }

  #onVisible;

  #render() {
    const cutoff = Number(this.dataset.cutoffHour || 14);
    const now = warsawNow();
    // Noon anchor avoids DST edge cases when only date math matters.
    let ship = new Date(now.year, now.month - 1, now.day, 12, 0, 0);

    let advanced = false;
    if (!isBusinessDay(ship) || now.hour >= cutoff) {
      ship = nextBusinessDay(ship);
      advanced = true;
    }
    const shipToday = !advanced;
    // Delivery is a range: ship + 1 to ship + 2 business days.
    const etaFrom = nextBusinessDay(ship);
    const etaTo = nextBusinessDay(etaFrom);

    const todayEl = this.querySelector('[data-when="today"]');
    const laterEl = this.querySelector('[data-when="later"]');
    const shipEl = this.querySelector('.pdp-delivery__ship');
    const etaEl = this.querySelector('.pdp-delivery__eta');

    if (todayEl) todayEl.hidden = !shipToday;
    if (laterEl) laterEl.hidden = shipToday;
    if (shipEl) shipEl.textContent = formatPolishDate(ship);
    if (etaEl) etaEl.innerHTML = formatDeliveryRange(etaFrom, etaTo);
  }
}
if (!customElements.get('pdp-delivery')) customElements.define('pdp-delivery', PdpDelivery);

/* ------------------------------------------------------------------ *
 * Add to cart — mirrors product-form.js JSON add flow so the native
 * cart-drawer refreshes (via cart-items-component) and auto-opens.
 * Used by "Skompletuj zestaw" rows.
 * ------------------------------------------------------------------ */
class PdpAddToCart extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    event.preventDefault();
    const variantId = this.dataset.variantId;
    if (!variantId || this.button?.disabled) return;

    const quantity = Number(this.dataset.quantity || 1);
    this.#setLoading(true);

    // Collect cart-items-component section ids so the drawer can re-render.
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], sections: sectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          deferred.resolve({ detail: { didError: true } });
          this.#flashError();
          return;
        }
        const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
        }).then((r) => r.json());

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartResponse),
          detail: {
            items: cartResponse.items,
            source: 'pdp-add-to-cart',
            sourceId: this.id?.toString() || 'pdp-add-to-cart',
            sections: response.sections,
            didError: false,
          },
        });
        this.#flashSuccess();
      })
      .catch((error) => {
        deferred.reject(error);
        this.#flashError();
      })
      .finally(() => this.#setLoading(false));
  };

  #setLoading(state) {
    if (!this.button) return;
    this.button.toggleAttribute('aria-busy', state);
    this.button.disabled = state;
  }

  #flashSuccess() {
    if (!this.button) return;
    const done = this.dataset.addedText || 'Dodano ✓';
    const original = this.button.dataset.label || this.button.textContent;
    this.button.dataset.label = original;
    this.button.textContent = done;
    setTimeout(() => {
      if (this.button) this.button.textContent = this.button.dataset.label || original;
    }, 1800);
  }

  #flashError() {
    if (!this.button) return;
    this.button.textContent = 'Spróbuj ponownie';
  }
}
if (!customElements.get('pdp-add-to-cart')) customElements.define('pdp-add-to-cart', PdpAddToCart);
